import React from 'react';
import LegalLayout from '../components/LegalLayout';
import SEO from '../components/SEO';

const Privacy: React.FC = () => {
    return (
        <LegalLayout>
            <SEO
                title="Privacy Policy"
                description="Our commitment to your privacy and security at Share Visuals."
            />
            <div className="animate-fade-in">
                {/* Header */}
                <div className="text-center mb-16 pb-12 border-b border-white/5 group">
                    <div className="inline-block bg-primary-500/10 border border-primary-500/20 text-primary-400 text-[10px] font-black uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8 group-hover:bg-primary-500/15 transition-colors">
                        Security & Trust
                    </div>
                    <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight leading-tight">Privacy Policy</h1>
                    <p className="text-dark-500 font-bold uppercase tracking-widest text-xs">Last updated: <span className="text-secondary-400">February 26, 2026</span></p>
                </div>

                {/* Intro Box */}
                <div className="relative mb-20">
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-3xl blur-md opacity-50" />
                    <div className="relative bg-[#0c1120] border border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-2xl bg-primary-500/20 flex items-center justify-center shrink-0 text-xl">🛡️</div>
                            <div className="text-dark-200 leading-relaxed text-lg font-medium italic">
                                <strong className="text-primary-400 not-italic block mb-1 uppercase text-xs tracking-widest">The Short Version</strong>
                                Share Visuals collects minimal data needed to run the service. We never sell your data, share it with advertisers, or use it for AI training without consent. We're an indie team that respects your space.
                            </div>
                        </div>
                    </div>
                </div>

                {/* TOC */}
                <div className="bg-white/[0.01] border border-white/5 rounded-3xl p-8 md:p-10 mb-24">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-dark-500 mb-8 flex items-center gap-3">
                        <span className="w-8 h-px bg-white/10" />
                        Table of Contents
                    </h4>
                    <ol className="grid md:grid-cols-2 gap-y-4 gap-x-12 list-decimal list-inside text-sm font-bold text-dark-400">
                        {[
                            'Information We Collect',
                            'How We Use Information',
                            'Data Storage and Security',
                            'Cookies and Tracking',
                            'Third-Party Services',
                            'Your Data Rights',
                            'Data Retention Policy',
                            'Children\'s Privacy',
                            'Policy Updates',
                            'Contact Privacy Team'
                        ].map((item, i) => (
                            <li key={i} className="hover:text-white transition-colors cursor-default border-b border-transparent hover:border-primary-500/30 pb-1">
                                <span className="text-dark-300 ml-2">{item}</span>
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Main Content Sections */}
                <div className="space-y-24">
                    {/* Section 1 */}
                    <section className="relative group">
                        <div className="absolute -left-12 top-0 h-full w-px bg-gradient-to-b from-primary-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />

                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">1</span>
                            Information We Collect
                        </h2>

                        <div className="space-y-8 text-dark-300">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4 p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                                    <h3 className="text-white font-bold flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                                        Account Data
                                    </h3>
                                    <p className="text-sm leading-relaxed">Required for identifying your subscription and saving your preferences. Includes email, username, and encrypted credentials.</p>
                                </div>
                                <div className="space-y-4 p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors">
                                    <h3 className="text-white font-bold flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                                        Usage Analytics
                                    </h3>
                                    <p className="text-sm leading-relaxed">Anonymous telemetry to help us understand which templates are trending and how to fix bugs faster.</p>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-primary-500/5 to-transparent border-l-4 border-primary-500 p-6 rounded-r-2xl">
                                <h3 className="text-white font-black text-sm mb-3 uppercase tracking-widest">Important: External Payments</h3>
                                <p className="text-dark-300 text-sm leading-relaxed">
                                    We use <strong className="text-white">DodoPayments</strong> for all transactions. Share Visuals <strong>never</strong> sees your full credit card number or CVV. We only receive a secure token confirming your payment was successful.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section className="relative group">
                        <div className="absolute -left-12 top-0 h-full w-px bg-gradient-to-b from-secondary-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity hidden md:block" />

                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">2</span>
                            How We Use Information
                        </h2>

                        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6">
                            {[
                                { title: 'Service Provision', desc: 'To keep the generator running and your history saved.' },
                                { title: 'Tier Enforcement', desc: 'To track daily generation limits accurately.' },
                                { title: 'Security', desc: 'To prevent abuse and keep your account safe.' },
                                { title: 'Communication', desc: 'Crucial product updates and support replies.' }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-4">
                                    <div className="mt-1 text-primary-400">✨</div>
                                    <div>
                                        <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                                        <p className="text-dark-500 text-xs leading-relaxed font-medium">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Section 3 Security */}
                    <section className="bg-gradient-to-br from-[#1E1B4B]/30 to-transparent border border-white/5 rounded-[40px] p-8 md:p-12">
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">3</span>
                            Data Storage and Security
                        </h2>
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div className="space-y-6">
                                <p className="text-dark-300 leading-relaxed font-medium">We host our data on <strong className="text-white">Supabase</strong> (powered by AWS). This means your data is protected by the same security protocols used by world-class tech companies.</p>
                                <ul className="space-y-3">
                                    {['AES-256 Encryption at Rest', 'SSL/TLS In-Transit', 'Isolated User Databases'].map((feat, i) => (
                                        <li key={i} className="flex items-center gap-3 text-xs font-black text-white/60 uppercase tracking-widest">
                                            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="bg-orange-500/10 border border-orange-500/20 p-8 rounded-3xl text-center">
                                <div className="text-3xl mb-4">🔐</div>
                                <h4 className="text-orange-400 font-bold mb-2">Security Advice</h4>
                                <p className="text-dark-400 text-xs leading-relaxed">Always use a unique password. We recommend using a password manager like Bitwarden or 1Password to keep your digital identity safe.</p>
                            </div>
                        </div>
                    </section>

                    {/* Section 4 Rights */}
                    <section>
                        <h2 className="text-3xl font-black text-white mb-8 flex items-center gap-5">
                            <span className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-black text-white shadow-lg shadow-primary-500/20">4</span>
                            Your Data Rights
                        </h2>
                        <div className="grid sm:grid-cols-3 gap-6">
                            {[
                                { icon: '👁️', title: 'Access', desc: 'See what we know' },
                                { icon: '✍️', title: 'Correct', desc: 'Fix old info' },
                                { icon: '🗑️', title: 'Delete', desc: 'Full account wipe' }
                            ].map((right, i) => (
                                <div key={i} className="bg-white/[0.02] border border-white/5 p-6 rounded-2xl text-center group cursor-default hover:bg-white/[0.04] transition-colors">
                                    <div className="text-2xl mb-3 group-hover:scale-110 transition-transform">{right.icon}</div>
                                    <h4 className="text-white font-bold text-sm mb-1">{right.title}</h4>
                                    <p className="text-dark-500 text-xs font-medium tracking-wide">{right.desc}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </LegalLayout>
    );
};

export default Privacy;
